/**
 * Feature Flag System
 *
 * Provides server-side guards for legacy/experimental features
 * Ensures production safety while keeping code in repository
 */

export type FeatureFlag = 'legacy-settings';

interface FeatureFlagOptions {
  adminOnly?: boolean;
  userRole?: 'admin' | 'owner' | 'member';
  allowInDev?: boolean;
}

/**
 * Check if a feature is enabled based on environment variables
 * Case-insensitive: accepts 'true', 'TRUE', 'True', etc.
 */
export function isFeatureEnabled(feature: FeatureFlag): boolean {
  const legacyFlag =
    import.meta.env.VITE_LEGACY_SETTINGS_ENABLED?.toLowerCase();

  switch (feature) {
    case 'legacy-settings':
      // In development, always enabled unless explicitly disabled
      if (import.meta.env.DEV) {
        return legacyFlag !== 'false';
      }
      // In production, must be explicitly enabled (case-insensitive)
      return legacyFlag === 'true';
    default:
      return false;
  }
}

/**
 * Assert that a feature is enabled, throw error if not
 * Use this in API routes / server actions to hard-block access
 */
export function assertFeatureEnabled(
  feature: FeatureFlag,
  opts?: FeatureFlagOptions
): void {
  const enabled = isFeatureEnabled(feature);

  // Feature completely disabled
  if (!enabled) {
    const error: any = new Error('Not found');
    error.statusCode = 404;
    throw error;
  }

  // Feature is admin-only
  if (opts?.adminOnly) {
    if (opts.userRole !== 'admin') {
      const error: any = new Error('Forbidden');
      error.statusCode = 403;
      throw error;
    }
  }
}

/**
 * Check if we should render a legacy feature
 * Returns false in production unless explicitly enabled via VITE_LEGACY_SETTINGS_ENABLED=true
 */
export function shouldRenderLegacyFeature(
  feature: FeatureFlag,
  userRole?: 'admin' | 'owner' | 'member'
): boolean {
  // Check the feature flag in all environments
  // In production, isFeatureEnabled requires explicit VITE_LEGACY_SETTINGS_ENABLED=true
  // In development, it's enabled by default unless explicitly set to 'false'
  return isFeatureEnabled(feature);
}
